import type { TimelineEvent } from '@/api/hooks';
import { Badge } from '@/components/ui/Badge';
import { eventTypeLabel, formatDate, formatDateTime } from '@/lib/format';
import { buildEventDetails, eventTypeMeta, primaryAnimal } from '@/lib/eventDisplay';
import { cn } from '@/lib/cn';
import { Link } from 'react-router-dom';

const toneClasses = {
  forest: 'bg-manejai-forest/10 text-manejai-forest',
  moss: 'bg-manejai-moss/10 text-manejai-moss',
  clay: 'bg-manejai-clay/10 text-manejai-clay',
  sage: 'bg-manejai-sage/20 text-manejai-forest',
};

export function EventListItem({ event }: { event: TimelineEvent }) {
  const meta = eventTypeMeta(event.event_type);
  const Icon = meta.icon;
  const animal = primaryAnimal(event);
  const details = buildEventDetails(event);

  return (
    <li className="px-5 py-4 hover:bg-manejai-cream/30 transition-colors">
      <div className="flex gap-4">
        <div
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
            toneClasses[meta.tone],
          )}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-manejai-forest">{eventTypeLabel(event.event_type)}</p>
                <span className="text-xs text-manejai-bark/40">#{event.event_id}</span>
              </div>
              <p className="text-sm text-manejai-bark/70 mt-0.5">{formatDate(event.event_date)}</p>
            </div>
            <div className="flex flex-wrap gap-1.5 justify-end">
              {details
                .filter((d) => d.highlight)
                .map((d) => (
                  <Badge key={d.label} tone="info">
                    {d.value}
                  </Badge>
                ))}
            </div>
          </div>

          {animal.label && (
            <div className="text-sm">
              <span className="text-manejai-bark/50">{animal.role}: </span>
              {animal.id ? (
                <Link
                  to={`/animais/${animal.id}`}
                  className="font-medium text-manejai-moss hover:text-manejai-forest hover:underline"
                >
                  {animal.label}
                </Link>
              ) : (
                <span className="font-medium text-manejai-bark">{animal.label}</span>
              )}
            </div>
          )}

          {details.filter((d) => !d.highlight).length > 0 && (
            <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
              {details
                .filter((d) => !d.highlight)
                .map((d) => (
                  <div key={d.label} className="flex gap-2 min-w-0">
                    <dt className="text-manejai-bark/50 shrink-0">{d.label}:</dt>
                    <dd className="text-manejai-bark truncate">{d.value}</dd>
                  </div>
                ))}
            </dl>
          )}

          {event.notes && (
            <p className="text-sm text-manejai-bark/70 rounded-lg bg-manejai-sand/30 px-3 py-2">
              {event.notes}
            </p>
          )}

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-manejai-bark/45">
            {event.responsible_name && <span>Responsável: {event.responsible_name}</span>}
            {event.created_by_name && <span>Registrado por: {event.created_by_name}</span>}
            {event.created_at && <span>Em {formatDateTime(event.created_at)}</span>}
          </div>
        </div>
      </div>
    </li>
  );
}
